// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseStatus, BaseTable, Loading } from '~/components/common';
// Others
import { IFormOrder } from '~/utils/interface/order';
import { IQuoteListItem } from '~/utils/interface/quotes';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IHeaderCellType } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { getListInventoryHistory } from '~/thunks/inventory/inventoryThunk';
import { DateFormatEnum, TypeInventoryGroupDay } from '~/utils/enum/common';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE } from '~/utils/constants/common';
import {
  IGroupDay,
  IInventoryHistory,
  IParamInventoryHistory,
  IQueryInventoryHistory,
} from '~/utils/interface/inventory';
// Styles, images, icons
import styles from './OrderItemDetailModal.module.scss';
import { icons } from '~/assets';

type Props = {
  dataInventory?: IQuoteListItem;
  day?: IGroupDay;
  type?: string;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const columnsInventoryHistory = (t: TFunction): IHeaderCellType<IInventoryHistory>[] => {
  return [
    {
      id: 'status',
      label: t('modal_orders_item_title_status'),
      render: (record: IInventoryHistory) => <BaseStatus label={record.status} />,
    },
    {
      id: 'orderNumber',
      label: t('modal_orders_item_title_transaction'),
      render: (record: IInventoryHistory) => (
        <Box className={cx('textCell')}>{record.orderNumber || record.orderId}</Box>
      ),
    },
    {
      id: 'startDate',
      label: t('modal_orders_item_title_client'),
      render: (record: IInventoryHistory) => <Box className={cx('textCell')}>{getFullName(record.client)}</Box>,
    },
    {
      id: 'startDate',
      label: t('modal_orders_item_title_date'),
      render: (record: IInventoryHistory) => (
        <Box className={cx('textCell')}>{formatDate(record.startDate, DateFormatEnum.MMM_D_YYYY)}</Box>
      ),
    },
    {
      id: 'quantity',
      label: t('modal_orders_item_title_quantity'),
      render: (record: IInventoryHistory) => <Box className={cx('textCell')}>{record.quantity}</Box>,
      align: 'center',
    },
    {
      id: 'total',
      label: t('modal_orders_item_title_total'),
      render: (record: IInventoryHistory) => (
        <Box className={cx('textCell')}>{formatCurrencyUSD({ value: record.total, showDecimal: true })}</Box>
      ),
    },
  ];
};

const OrderItemDetailModal = (props: Props) => {
  //#region Destructuring Props
  const { dataInventory, day, type, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();

  const { watch } = useFormContext<IFormOrder>();
  const { startDate, endDate } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [scroll] = useState<DialogProps['scroll']>('paper');
  const [listInventoryHistory, setListInventoryHistory] = useState<IInventoryHistory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryInventoryHistory>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
    type: type,
    startDate,
    endDate,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!dataInventory || !paramObject) return;

    const params: IParamInventoryHistory = {
      inventoryId: dataInventory.id,
      query: paramObject,
    };

    handleGetListInventoryHistory(params);
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInventoryHistory = (params: IParamInventoryHistory) => {
    setIsLoading(true);

    dispatch(getListInventoryHistory(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res?.data;

        if (!responses) return;

        setListInventoryHistory(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    onClose && onClose();
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <Dialog id='activityDetailComponent' open={true} onClose={handleCloseModal} fullWidth maxWidth={'md'}>
      <Stack className={cx('viewHeader')}>
        <Stack>
          <Typography className={cx('titleHeader')}>{dataInventory?.name}</Typography>
          <Typography className={cx('titleSubHeader')}>
            {t('modal_orders_item_title_orders_for', {
              name:
                type === TypeInventoryGroupDay.ORDER
                  ? t('modal_orders_item_title_orders')
                  : t('modal_orders_item_title_quotes'),
              value: formatDate(day?.day, DateFormatEnum.MMM_D),
            })}
          </Typography>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonIcon')}>
          <img src={icons.commonIconCancel} alt={t('common_img_text_alt')} className={cx('iconCancel')} />
        </IconButton>
      </Stack>

      <DialogContent dividers={scroll === 'paper'} className={cx('body')}>
        <BaseTable
          dataSource={listInventoryHistory}
          currentPage={paramObject.page}
          totalPage={pagination?.totalPages}
          columns={columnsInventoryHistory(t)}
          onPageChange={handlePageChange}
        />
      </DialogContent>

      <DialogActions>
        <Button variant='contained' onClick={handleCloseModal} className={cx('buttonClose')}>
          {t('common_action_close', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default OrderItemDetailModal;
